import { Component, inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Order } from '../../models/order.model';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { RolesEnum } from '../../enums/roles.enum';
import { Location } from "@angular/common";
import { OrderState } from "../../enums/order-state.enum";
import { first, map } from "rxjs/operators";
import { ForceStatusComponent } from "../force-status/force-status.component";
import { Subscription, switchMap } from "rxjs";
import { Clipboard } from "@angular/cdk/clipboard";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SettingsService } from "../../services/settings.service";
import { MatDialog } from "@angular/material/dialog";
import { OrderService } from "../../services/order.service";
import { TranslateService } from "@ngx-translate/core";
import { RestoreOrderComponent } from "../restore-order/restore-order.component";
import { OrganizationService } from "../../services/organization.service";
import { ConfirmEmailComponent } from "../confirm-email/confirm-email.component";
import { FileTask } from "../../models/file-task.model";
import { FileImportTask } from "../../models/file-import-task.model";
import { NestingTask } from "../../models/nesting-task.model";
import { DeleteOrderComponent } from "../delete-order/delete-order.component";
import { ProcessingErrorEnum } from "../../enums/processing-error.enum";
import { SourceType } from "../../enums/source-type.enum";
import { DownloadOrderFilesComponent } from "../download-order-files/download-order-files.component";
import { PrintOrderComponent } from "../print-order/print-order.component";
import { OrderInformationComponent } from "../order-information/order-information.component";

@Component({
    selector: 'app-order-navigation',
    templateUrl: './order-navigation.component.html',
    styleUrls: ['./order-navigation.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OrderNavigationComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private clipboard = inject(Clipboard);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private orderService = inject(OrderService);
  private settingsService = inject(SettingsService);
  public location = inject(Location);
  public authService = inject(AuthService);
  public translate = inject(TranslateService);
  public organizationService = inject(OrganizationService);
  @Input() order: Order;
  @Input() withTransport = false;
  @Input() withFiles = false;
  @Input() withBat = false;
  @Input() tableMode = false;
  @Input() withBackButton = true;
  @Input() selectable = false;
  @Input() icon = 'more_vert';
  public RolesEnum = RolesEnum;
  public progress = false;
  public zipProgress = false;
  public productionFileProgress = false;
  protected readonly OrderState = OrderState;
  public fileTask: FileTask;
  public importTask: FileImportTask;
  public nestingTask: NestingTask;
  private taskSub: Subscription;
  private importTaskSub: Subscription;
  private nestingTaskSub: Subscription;
  protected readonly SourceType = SourceType;
  isRotating = false;

  ngOnInit(): void {
    this.getFilesImports();
    this.getFilesProcessing();
    this.getNestingTasks();
  }

  ngOnDestroy(): void {
    if (this.taskSub) {
      this.taskSub.unsubscribe();
    }
    if (this.importTaskSub) {
      this.importTaskSub.unsubscribe();
    }
    if (this.nestingTaskSub) {
      this.nestingTaskSub.unsubscribe();
    }
  }

  /**
   * close popup without any actions
   * @param type type of requested link
   * @param event The click event
   */
  getLink(type: number, event: MouseEvent): void {
    event.stopPropagation();
    this.progress = true;
    this.orderService.getAccessToken(this.order.id, type)
      .then(url => {
        this.clipboard.copy(url);
        const message = this.translate.instant('ADMIN.COPY_LINK_SUCCESS');
        this.snackBar.open(message + '\n\n' + url, 'x', {
          duration: 10000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      })
      .catch(err => {
        console.error(err);
        throw err;
      })
      .finally(() => this.progress = false);
  }

  /**
   * Open the local network folder corresponding to the selected order
   * @param order The selected order
   */
  openCustomerFolder(order: Order): void {
    this.settingsService.getSambaOrderPath(order).then(path => {
      const a = document.createElement('a');
      a.setAttribute('href', path);
      a.click();
    });
  }

  /**
   * Open dialog to force order to state "Wait for validation" or
   * "BAT validated"
   */
  forceWaitingValidation(): void {
    const ref = this.dialog.open(ForceStatusComponent, {
      data: { order: this.order },
    });
    ref.afterClosed()
      .pipe(
        first(),
        switchMap(res => {
          if (res) {
            this.order.state = this.order.batIsActive ? OrderState.WAIT_BAT : OrderState.BAT_ACCEPTED;
            return this.router.navigate([this.organizationService.currentOrganization.oid, 'orders']);
          }
        }))
      .subscribe();
  }

  /**
   * Open unlock order popup
   * @param order The selected order
   */
  openUnlockOrderDialog(order: Order): void {
    this.dialog.open(RestoreOrderComponent, { data: order });
  }

  /**
   * Set the order to print
   */
  print(): void {
    this.dialog.open(PrintOrderComponent, { data: this.order });
  }

  /**
   * Create order files processing task
   * @param order The selected order
   * @param mode The popup mode
   */
  sendFileEmail(order: Order, mode: number): void {
    this.dialog.open(ConfirmEmailComponent, {
      data: { order, mode },
    });
  }

  /**
   * Get processing errors of a product
   * @returns the list of processing errors
   */
  hasErrors(): boolean {
    if (!this.order) {
      return false;
    }
    for (const product of this.order.products) {
      const error = this.order.qualityErrors
        .find(qualityError => qualityError.poId === product.poId);
      if (error) {
        return true;
      }
    }
    return this.order.fileCheckErrorCode === ProcessingErrorEnum.TIMEOUT ||
      this.order.fileCheckErrorCode === ProcessingErrorEnum.LOCAL_EXEC ||
      this.order.fileCheckErrorCode === ProcessingErrorEnum.DOWNLOAD_ERROR ||
      this.order.fileCheckErrorCode === ProcessingErrorEnum.MANUAL_PROOFS ||
      this.order.fileCheckErrorCode === ProcessingErrorEnum.MANUAL_CHECK ||
      this.order.fileCheckErrorCode === ProcessingErrorEnum.UPLOAD_ERROR;
  }

  /**
   * Open remove order popup
   * @param order The selected order
   */
  openDeleteOrderDialog(order: Order): void {
    this.dialog.open(DeleteOrderComponent, { data: order }).afterClosed()
      .subscribe(res => {
        if (res) {
          if (!this.router.url.includes('orders')) {
            this.location.back()
          }
        }
      });
  }

  /**
   * Subscribe to files processing task
   */
  private getFilesProcessing(): void {
    this.taskSub = this.orderService.getFilesTasks()
      .pipe(map(tasks => tasks
        .find(task => task.orderId === this.order.id)))
      .subscribe(fileTasks => this.fileTask = fileTasks);
  }

  /**
   * Subscribe to files import task
   */
  private getFilesImports(): void {
    this.importTaskSub = this.orderService.getImportTasks()
      .pipe(map(tasks => tasks
        .find(task => task.orderId === this.order.id)))
      .subscribe(importTasks => this.importTask = importTasks);
  }

  /**
   * Subscribe to nesting tasks for this order and split into active and failed
   */
  private getNestingTasks(): void {
    this.nestingTaskSub = this.orderService.getNestingTasks()
      .pipe(map(tasks => tasks.filter(task => task.transactionId === this.order.transactionId)))
      .subscribe(tasks => {
        this.nestingTask = tasks.find(t => t.inProgress || t.withError);
      });
  }

  /**
   * Returns the tooltip message for the error icon, covering both file errors and nesting errors.
   * @returns The translated error message
   */
  get errorTooltip(): string {
    if (this.nestingTask?.withError) {
      return this.translate.instant('NESTING_FAILED');
    }
    if (!this.order.fileCheckErrorCode) {
      return '';
    }
    const fileError = this.translate.instant(this.order.fileCheckErrorCode);
    return this.order.nesting
      ? `${fileError} ${this.translate.instant('NESTING_MANUAL')}`
      : fileError;
  }

  /**
   * Synchronizes the order for the given transaction ID with the organization's order service.
   * @param order - The unique identifier for the transaction to be synchronized.
   * @returns A promise that resolves when the order synchronization process completes.
   */
  async sync(order: Order): Promise<boolean> {
    this.isRotating = true;
    try {
      await this.orderService.syncOrder(this.organizationService.currentOrganization.id, order.transactionId);
    } catch (error) {
      console.error(error);
    } finally {
      this.isRotating = false;
    }
    const currentUrl = this.router.url;
    return this.router.navigateByUrl(currentUrl);
  }

  /**
   * Whether a Callas file task, import task or nesting task is currently running.
   * @returns True when at least one background processing task is in progress
   */
  isProcessingInProgress(): boolean {
    return !!(this.fileTask || this.importTask || this.nestingTask?.inProgress);
  }

  /**
   * Determines if there are any manual proofs required for the order.
   * This method checks the products in the order to identify if any of them lack a profile,
   * indicating that manual proofs are needed.
   * @returns True if at least one product requires manual proof, false otherwise.
   */
  hasManualProofs(): boolean {
    return !!this.order.products.find(p => !p.profile);
  }

  /**
   * Opens a dialog to facilitate the download of order files.
   * The method launches a component that handles the file export functionality for the specified order.
   */
  download(): void {
    this.dialog.open(DownloadOrderFilesComponent, { data: { order: this.order, product: null } })
  }

  /**
   * Opens a popup dialog to show the complete order information.
   */
  openOrderInformationDialog(): void {
    this.dialog.open(OrderInformationComponent, {
      data: { order: this.order },
      width: '780px',
      maxWidth: '95vw',
      panelClass: 'premium-order-info-dialog'
    });
  }

  /**
   * Navigates to the specified order page based on the current organization's ID and the order ID.
   * @returns A promise that resolves to a boolean indicating whether the navigation was successful.
   */
  goTo(): Promise<boolean> {
    return this.router.navigate([this.organizationService.currentOrganization.oid, 'order', this.order.id]);
  }

  /**
   * Downloads the production file associated with the current order and opens it in a new browser tab.
   * The file is retrieved from the server as a base64-encoded string, decoded, and converted into a PDF Blob.
   * @returns A Promise that resolves when the download process is completed.
   */
  async downloadProductionFile(): Promise<void> {
    this.productionFileProgress = true;
    try {
      const base64String = await this.orderService.download(this.order.id);
      const binaryString = atob(base64String);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes.buffer], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } finally {
      this.productionFileProgress = false;
    }
  }
}
